import { useEffect, type ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import { useLocation } from 'react-router-dom';
import {
  APP_SHELL_COLLAPSED_LEFT_SIZE,
  APP_SHELL_COLLAPSED_MIN_RIGHT_SIZE,
  APP_SHELL_DEFAULT_LEFT_SIZE,
  APP_SHELL_MAX_LEFT_SIZE,
  APP_SHELL_MIN_LEFT_SIZE,
  APP_SHELL_MIN_RIGHT_SIZE,
} from '@/shared/config';
import { LeftHoverSidebar, ResizableSplitPane } from '@/shared/ui/primitives';
import {
  getPanelSizes,
  setPanelSizes,
  toggleLeftSidebar,
  closeLeftSidebar,
  $copilotCollapsed,
} from '@/shared/stores/ui';

interface AppShellProps {
  /** Content for the left workspace panel */
  leftWorkspace: ReactNode;
  /** Content for the right copilot panel */
  rightCopilot: ReactNode;
  /** Optional header rendered above the split panels */
  header?: ReactNode;
  /** Optional content for the left hover sidebar overlay */
  leftOverlay?: ReactNode;
  /** Optional ID for panel size persistence */
  persistenceId?: string;
}

/**
 * AppShell — the top-level layout primitive for the clinician copilot.
 *
 * Provides a stable two-panel shell with:
 * - Left workspace (patient record, timeline, problem tree, etc.)
 * - Right copilot (AI assistant, followups, notepad, etc.)
 * - Optional left hover sidebar overlay (patient navigator, etc.)
 *
 * The shell owns structure and resizing; feature/widget code must not
 * re-implement shell behavior.
 *
 * @remarks
 * - Panel sizes are persisted via nanostores when persistenceId is provided
 * - Resize handle is keyboard accessible
 * - Layout is stable across route changes (no remount flicker)
 *
 * @example
 * ```tsx
 * <AppShell
 *   header={<GlobalHeader />}
 *   leftWorkspace={<PatientPanel />}
 *   rightCopilot={<CopilotPanel />}
 * />
 * ```
 */
export function AppShell({
  leftWorkspace,
  rightCopilot,
  header,
  leftOverlay,
  persistenceId,
}: AppShellProps) {
  const isCollapsed = useStore($copilotCollapsed);
  const location = useLocation();

  useEffect(() => {
    closeLeftSidebar();
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        return;
      }
      if (e.key.toLowerCase() !== 'k') {
        return;
      }

      e.preventDefault();
      toggleLeftSidebar();
    };

    const options = { capture: true } as const;
    window.addEventListener('keydown', onKeyDown, options);
    return () => {
      window.removeEventListener('keydown', onKeyDown, options);
    };
  }, []);

  const persistedFirstSize =
    persistenceId === undefined
      ? APP_SHELL_DEFAULT_LEFT_SIZE
      : getPanelSizes(persistenceId, {
          first: APP_SHELL_DEFAULT_LEFT_SIZE,
          second: 100 - APP_SHELL_DEFAULT_LEFT_SIZE,
        }).first;

  const split = isCollapsed
    ? {
        defaultFirstSize: APP_SHELL_COLLAPSED_LEFT_SIZE,
        minFirstSize: APP_SHELL_COLLAPSED_LEFT_SIZE,
        maxFirstSize: APP_SHELL_COLLAPSED_LEFT_SIZE,
        minSecondSize: APP_SHELL_COLLAPSED_MIN_RIGHT_SIZE,
        withHandle: false,
        second: rightCopilot,
        onResize: undefined,
      }
    : {
        defaultFirstSize: persistedFirstSize,
        minFirstSize: APP_SHELL_MIN_LEFT_SIZE,
        maxFirstSize: APP_SHELL_MAX_LEFT_SIZE,
        minSecondSize: APP_SHELL_MIN_RIGHT_SIZE,
        withHandle: true,
        second: rightCopilot,
        onResize: (sizes: { first: number; second: number }) => {
          if (persistenceId !== undefined) {
            setPanelSizes(persistenceId, sizes);
          }
        },
      };

  return (
    <div className="flex h-screen w-screen flex-col bg-background">
      {header !== undefined && header}
      <main className="flex-1 overflow-hidden">
        <ResizableSplitPane
          id={persistenceId}
          direction="horizontal"
          defaultFirstSize={split.defaultFirstSize}
          minFirstSize={split.minFirstSize}
          maxFirstSize={split.maxFirstSize}
          minSecondSize={split.minSecondSize}
          withHandle={split.withHandle}
          onResize={split.onResize}
          first={leftWorkspace}
          second={split.second}
        />
      </main>

      {/* Left hover sidebar overlay */}
      {leftOverlay !== undefined && <LeftHoverSidebar>{leftOverlay}</LeftHoverSidebar>}
    </div>
  );
}
